import { createQuery } from '@tanstack/solid-query'
import type {
  RecordDocuments,
  RecordEntity,
  RecordFile,
  RecordNoteInput,
  RecordUploadInput,
} from '@veem/crm-schema'
import { ApiError, api } from '../../lib/api.ts'
import { appendPickedFile } from '../../lib/file-reference.ts'
import { queryKeys } from '../../lib/query-keys.ts'
import { session } from '../../lib/session.ts'
import type { FileKind } from '../../prototype/data/orders.ts'
import { proto } from '../../prototype/state/store.ts'

const moduleOf: Record<RecordEntity, string> = {
  task: 'tasks',
  order: 'orders',
  customer: 'customers',
  supplier: 'suppliers',
  internal_order: 'production',
  engineering: 'engineering',
}
const recordPath = (entity: RecordEntity, entityId: string) =>
  `/records/${entity}/${encodeURIComponent(entityId)}`
const fileRequests = new WeakMap<File, Map<string, string>>()
const noteRequests = new Map<string, string>()
const retryId = (key: string, requests: Map<string, string>) => {
  const existing = requests.get(key)
  if (existing) return existing
  const id = crypto.randomUUID()
  requests.set(key, id)
  return id
}

export const createRecordDocumentsQuery = (
  scope: () => { entity: RecordEntity; entityId: string },
) =>
  createQuery(() => {
    const current = scope()
    return {
      queryKey: [
        ...queryKeys.module(moduleOf[current.entity]),
        'records',
        session()?.actor.id,
        current.entity,
        current.entityId,
      ],
      queryFn: () => api<RecordDocuments>(recordPath(current.entity, current.entityId)),
      enabled: !!session() && !!current.entityId,
    }
  })

const postForm = async <T>(path: string, body: FormData): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`/api${path}`, { method: 'POST', credentials: 'include', body })
  } catch {
    throw new ApiError('Немає зв’язку із сервером. Спробуйте ще раз.', 0, 'NETWORK_ERROR')
  }
  let result: {
    ok: boolean
    data: T
    error?: { code: string; message: string }
  }
  try {
    result = (await response.json()) as typeof result
  } catch {
    throw new ApiError(
      'Сервер тимчасово недоступний. Спробуйте ще раз.',
      response.status,
      'INVALID_RESPONSE',
    )
  }
  if (!response.ok || !result.ok)
    throw new ApiError(
      result.error?.message ?? 'Не вдалося завантажити файл',
      response.status,
      result.error?.code ?? 'UPLOAD_FAILED',
    )
  return result.data
}

export const uploadRecordFile = async (
  input: Omit<RecordUploadInput, 'requestId'> & { requestId?: string },
  file: File,
) => {
  const body = new FormData()
  await appendPickedFile(body, 'file', file)
  body.set('category', input.category ?? 'other')
  const requests = fileRequests.get(file) ?? new Map<string, string>()
  fileRequests.set(file, requests)
  body.set(
    'requestId',
    input.requestId ??
      retryId(
        `${session()?.actor.id}:${input.entity}:${input.entityId}:${input.category}`,
        requests,
      ),
  )
  return postForm<RecordFile>(`${recordPath(input.entity, input.entityId)}/files`, body)
}

export const addRecordNote = async (
  input: Omit<RecordNoteInput, 'requestId'> & { requestId?: string },
  file?: File,
) => {
  const { entity, entityId, ...note } = input
  const retryKey = JSON.stringify({
    actor: session()?.actor.id,
    ...input,
    file: file ? [file.name, file.size, file.lastModified] : null,
  })
  if (noteRequests.size > 200) noteRequests.clear()
  const metadata = { ...note, requestId: input.requestId ?? retryId(retryKey, noteRequests) }
  let result: unknown
  if (file) {
    const body = new FormData()
    await appendPickedFile(body, 'file', file)
    body.set('note', JSON.stringify(metadata))
    result = await postForm(`${recordPath(entity, entityId)}/notes`, body)
  } else {
    result = await api(`${recordPath(entity, entityId)}/notes`, {
      method: 'POST',
      body: JSON.stringify(metadata),
    })
  }
  noteRequests.delete(retryKey)
  return result
}

export const removeRecordFile = (fileId: string, version: number) =>
  api(`/records/files/${encodeURIComponent(fileId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ version }),
  })

export const addOrderFile = async (dealId: string, file: File, kind: FileKind) => {
  await uploadRecordFile({ entity: 'order', entityId: dealId, category: kind }, file)
}

export const removeOrderFile = async (dealId: string, fileId: string) => {
  const file = proto.state.orders
    .find((order) => order.deal.id === dealId)
    ?.files.find((candidate) => candidate.id === fileId) as { version?: number } | undefined
  if (!file) throw new ApiError('Файл не знайдено', 404, 'NOT_FOUND')
  await removeRecordFile(fileId, file.version ?? 1)
}

export const addOrderNote = async (
  dealId: string,
  text: string,
  kind: 'note' | 'call' | 'email' = 'note',
  mentions: string[] = [],
  file?: File,
) => {
  await addRecordNote({ entity: 'order', entityId: dealId, text, kind, mentions }, file)
}
